/**
 * The one place the IndexedDB schema is declared.
 *
 * Two stores share a database because they have to be opened together — a second
 * `indexedDB.open` at a different version from the same tab deadlocks against the first.
 * Keeping the schema here means `onupgradeneeded` is written once and both stores are always
 * created, rather than each module racing to add its own.
 */

export const DB_NAME = "sportsos";
export const DB_VERSION = 2;

/** The ball ledger. Keyed by `eventId`, which makes `append` a natural upsert. */
export const EVENTS = "events";
export const EVENTS_BY_MATCH = "byMatch";

/**
 * Match setup: teams, squads, toss, RuleSet.
 *
 * Not ledger events, and deliberately a separate store. A squad is not something that
 * *happened* — it is the frame the events are interpreted in, and modelling it as an event
 * would put a roster edit into the ball-by-ball log.
 *
 * It has to be local for the same reason the ledger does: without it a reload finds 250 balls
 * and no idea whose. Once #14 lands the server is the durable copy and this becomes a cache;
 * until then it is the only copy.
 */
export const MATCHES = "matches";
/** Single-row store holding which match this device is currently scoring. */
export const CURRENT = "current";

export const asPromise = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = (): void => resolve(request.result);
    request.onerror = (): void => reject(request.error ?? new Error("IndexedDB request failed"));
  });

/**
 * A transaction's *completion*, not its request's success.
 *
 * The distinction is the point of the whole ticket. `put` reporting success means the write is
 * queued inside the transaction; `oncomplete` means it is durable. Awaiting the wrong one
 * turns "the ball is saved" from a fact into a claim.
 */
export const committed = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = (): void => resolve();
    tx.onabort = (): void => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
    tx.onerror = (): void => reject(tx.error ?? new Error("IndexedDB transaction failed"));
  });

let opening: Promise<IDBDatabase> | null = null;

export function database(): Promise<IDBDatabase> {
  opening ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (): void => {
      const db = request.result;
      if (!db.objectStoreNames.contains(EVENTS)) {
        const store = db.createObjectStore(EVENTS, { keyPath: "eventId" });
        store.createIndex(EVENTS_BY_MATCH, "matchId", { unique: false });
      }
      if (!db.objectStoreNames.contains(MATCHES)) {
        db.createObjectStore(MATCHES, { keyPath: "matchId" });
      }
      if (!db.objectStoreNames.contains(CURRENT)) {
        db.createObjectStore(CURRENT);
      }
    };
    request.onsuccess = (): void => resolve(request.result);
    request.onerror = (): void => reject(request.error ?? new Error("could not open IndexedDB"));
  });
  return opening;
}

/** Whether this browser can persist at all. A hard failure, never a silent downgrade. */
export const canPersist = (): boolean => typeof indexedDB !== "undefined" && indexedDB !== null;
